import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';

interface SkillInventoryItem {
  skillId?: number;
  skill: string;
  totalEmployees: number;
  byProficiency: {
    strong: number;
    working: number;
    awareness: number;
  };
  departments: string[];
  trend: 'increasing' | 'decreasing' | 'stable';
}

interface SkillsInventorySectionProps {
  data: SkillInventoryItem[];
  expanded?: boolean;
  onViewMore?: () => void;
  /** True when the page's filter bar is narrowing this list, which changes what an empty list means. */
  filtersActive?: boolean;
  onClearFilters?: () => void;
}

const SkillsInventorySection: React.FC<SkillsInventorySectionProps> = ({
  data,
  expanded = false,
  onViewMore,
  filtersActive = false,
  onClearFilters,
}) => {
  const { t } = useTranslation();

  // Percentage of a total, guarded: an unheld catalogue skill has a total of 0.
  const share = (count: number, total: number) => (total > 0 ? (count / total) * 100 : 0);

  const getTrendIcon = (trend: string) => {
    if (trend === 'increasing') return { icon: 'TrendingUp', color: 'text-green-500' };
    if (trend === 'decreasing') return { icon: 'TrendingDown', color: 'text-red-500' };
    return { icon: 'Minus', color: 'text-gray-400' };
  };

  const getProficiencyColor = (proficiency: string) => {
    switch (proficiency) {
      case 'Strong':
        return 'bg-green-500';
      case 'Working':
        return 'bg-blue-500';
      case 'Awareness':
        return 'bg-gray-400';
      default:
        return 'bg-gray-300';
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground flex items-center">
            <Icon name="Award" size={24} className="me-2 text-blue-600" />
            {t('Skills Inventory')}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">{t('Aggregated skill distribution across the organization')}</p>
        </div>
        <span className="text-sm text-muted-foreground">{data?.length} {t('skills tracked')}</span>
      </div>

      {/* Search makes an empty list a routine outcome, so it needs an answer rather than blank space. */}
      {(data?.length ?? 0) === 0 && (
        <div
          className="rounded-lg border border-dashed border-border p-8 text-center"
          data-testid="skills-inventory-empty"
        >
          <Icon name={filtersActive ? 'SearchX' : 'Award'} size={28} className="mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {filtersActive ? t('skillsOverview.noSkillsMatch') : t('skillsOverview.noSkillsYet')}
          </p>
          {filtersActive && onClearFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="mt-3 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 rounded"
            >
              {t('Clear filters')}
            </button>
          )}
        </div>
      )}

      <div className="space-y-4">
        {data?.slice(0, expanded ? data?.length : 3)?.map((skill, index) => (
          <div
            key={index}
            className="border border-border rounded-lg p-4 hover:border-primary/50 hover:shadow-sm transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-base font-semibold text-gray-900">{skill?.skill}</h3>
                  <div className="flex items-center space-x-1">
                    <Icon name={getTrendIcon(skill?.trend)?.icon as any} size={16} className={getTrendIcon(skill?.trend)?.color} />
                  </div>
                </div>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span className="flex items-center">
                    <Icon name="Users" size={12} className="mr-1" />
                    {skill?.totalEmployees} {t('employees')}
                  </span>
                  <span className="flex items-center">
                    <Icon name="Building2" size={12} className="mr-1" />
                    {skill?.departments?.length} {t('departments')}
                  </span>
                </div>
              </div>
            </div>

            {/* Proficiency Distribution Bar */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                <span>{t('Proficiency Distribution')}</span>
                <span>{skill?.totalEmployees} {t('total')}</span>
              </div>
              {/* A catalogue skill nobody holds yet is a real row with a total of 0. Dividing by that
                  total yielded `width: NaN%`, so the bar is replaced by an explicit empty state and
                  the share maths only runs when there is someone to divide by. */}
              {skill?.totalEmployees > 0 ? (
                <div className="flex h-6 rounded-lg overflow-hidden">
                  <div
                    className={`${getProficiencyColor('Strong')} flex items-center justify-center text-white text-xs font-medium`}
                    style={{ width: `${share(skill?.byProficiency?.strong, skill?.totalEmployees)}%` }}
                  >
                    {skill?.byProficiency?.strong > 0 && skill?.byProficiency?.strong}
                  </div>
                  <div
                    className={`${getProficiencyColor('Working')} flex items-center justify-center text-white text-xs font-medium`}
                    style={{ width: `${share(skill?.byProficiency?.working, skill?.totalEmployees)}%` }}
                  >
                    {skill?.byProficiency?.working > 0 && skill?.byProficiency?.working}
                  </div>
                  <div
                    className={`${getProficiencyColor('Awareness')} flex items-center justify-center text-white text-xs font-medium`}
                    style={{ width: `${share(skill?.byProficiency?.awareness, skill?.totalEmployees)}%` }}
                  >
                    {skill?.byProficiency?.awareness > 0 && skill?.byProficiency?.awareness}
                  </div>
                </div>
              ) : (
                <div
                  className="flex h-6 items-center justify-center rounded-lg border border-dashed border-border text-xs text-muted-foreground"
                  data-testid={`skill-unassigned-${skill?.skillId ?? index}`}
                >
                  {t('skillsOverview.noHoldersYet')}
                </div>
              )}
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center space-x-4 text-xs">
                  <span className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded mr-1" />
                    {t('Strong')} ({skill?.byProficiency?.strong})
                  </span>
                  <span className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded mr-1" />
                    {t('Working')} ({skill?.byProficiency?.working})
                  </span>
                  <span className="flex items-center">
                    <div className="w-3 h-3 bg-gray-400 rounded mr-1" />
                    {t('Awareness')} ({skill?.byProficiency?.awareness})
                  </span>
                </div>
              </div>
            </div>

            {/* Departments */}
            <div className="flex flex-wrap gap-2">
              {skill?.departments?.map((dept, deptIndex) => (
                <span
                  key={deptIndex}
                  className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                >
                  {dept}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {!expanded && data?.length > 3 && (
        <div className="mt-4 text-center">
          {onViewMore ? (
            <button
              onClick={onViewMore}
              className="text-primary hover:text-primary/80 text-sm font-medium"
            >
              {t('View All')} {data?.length} {t('Skills')}
            </button>
          ) : (
            <p className="text-sm text-muted-foreground">{t('Showing')} 3 {t('of')} {data?.length} {t('skills')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default SkillsInventorySection;
